import { useEffect, useState } from 'react';

import { API_BASE_URL } from '../config/env';
import type { LiveSnapshot } from '../lib/closing';

/**
 * One SSE connection, for one investment.
 *
 * SCOPED PER INVESTMENT ON PURPOSE. The hook takes a single id and opens its own
 * EventSource for it, so an investor with several deals open across tabs or monitors has
 * one independent connection per deal. Each carries only its own numbers, and one deal
 * going quiet, being cancelled, or losing its connection leaves the others untouched.
 *
 * THE INDICATOR IS HONEST. `connected` is driven by the EventSource's actual readyState
 * transitions, not by whether we once asked for a connection. It goes false the moment
 * the socket errors and only returns when the browser genuinely reopens it, so a dot that
 * says "Live" is a dot backed by an open stream. `lastUpdateAt` is stamped only when a
 * real payload arrives, never on a heartbeat, so "updated Xs ago" means what it says.
 */
export interface LiveState {
  connected: boolean;
  snapshot: LiveSnapshot | null;
  lastUpdateAt: number | null;
  /** Set when the server closed the stream deliberately, e.g. the holding is no longer active. */
  closedReason: string | null;
}

export function useLiveLedger(investmentId: string | null, enabled: boolean): LiveState {
  const [connected, setConnected] = useState(false);
  const [snapshot, setSnapshot] = useState<LiveSnapshot | null>(null);
  const [lastUpdateAt, setLastUpdateAt] = useState<number | null>(null);
  const [closedReason, setClosedReason] = useState<string | null>(null);

  useEffect(() => {
    if (!investmentId || !enabled) return undefined;

    // withCredentials so the auth cookie rides along. The API is same-origin (the portal
    // proxies /api), which is what makes an httpOnly cookie usable here at all:
    // EventSource cannot set an Authorization header.
    const es = new EventSource(
      `${API_BASE_URL}/investments/${investmentId}/live`,
      { withCredentials: true },
    );

    es.onopen = () => { setConnected(true); setClosedReason(null); };

    es.onerror = () => {
      // The browser retries on its own. Reflect the real state meanwhile rather than
      // leaving a "Live" dot lit over a dead socket.
      setConnected(es.readyState === EventSource.OPEN);
    };

    const onPayload = (ev: Event) => {
      const msg = ev as MessageEvent<string>;
      try {
        setSnapshot(JSON.parse(msg.data) as LiveSnapshot);
        setLastUpdateAt(Date.now());
        setConnected(true);
      } catch {
        // A malformed frame is not worth tearing the stream down for.
      }
    };

    const onClosed = (ev: Event) => {
      const msg = ev as MessageEvent<string>;
      let reason = 'unavailable';
      try {
        reason = (JSON.parse(msg.data) as { reason?: string }).reason ?? reason;
      } catch { /* keep the default */ }
      setClosedReason(reason);
      setConnected(false);
      // Deliberate close by the server. Stop the browser reconnecting into the same
      // refusal every few seconds.
      es.close();
    };

    es.addEventListener('position', onPayload);
    es.addEventListener('status', onPayload);
    es.addEventListener('closed', onClosed);

    return () => {
      es.removeEventListener('position', onPayload);
      es.removeEventListener('status', onPayload);
      es.removeEventListener('closed', onClosed);
      es.close();
      setConnected(false);
    };
  }, [investmentId, enabled]);

  return { connected, snapshot, lastUpdateAt, closedReason };
}
